import React, { createContext, useEffect, useState } from 'react'
import { kpiMetrics } from 'core/constants/kpi'
import { PERIOD } from 'core/constants/period'
import { getPeriodDates } from 'core/utils/service'
import { IKpiResponse, IMetric } from 'core/interfaces/Kpi'
import { IPromptDetail } from 'core/interfaces/Prompt'

interface KpiPromptContextType {
  activeMetric: IMetric
  setActiveMetric: React.Dispatch<React.SetStateAction<IMetric>>
  period: string
  setPeriod: React.Dispatch<React.SetStateAction<string>>
  currentDate: Date
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>
  start: Date
  setStart: React.Dispatch<React.SetStateAction<Date>>
  end: Date
  setEnd: React.Dispatch<React.SetStateAction<Date>>
  selectedPrompts: number[]
  setSelectedPrompts: React.Dispatch<React.SetStateAction<number[]>>
  promptsList: IPromptDetail[]
  setPromptsList: React.Dispatch<React.SetStateAction<IPromptDetail[]>>
  kpis: Record<string, IKpiResponse[]>
  setKpis: React.Dispatch<React.SetStateAction<Record<string, IKpiResponse[]>>>
  oldGlobalKpis: IKpiResponse[]
  setOldGlobalKpis: React.Dispatch<React.SetStateAction<IKpiResponse[]>>
  valueType: 'TOTAL' | 'AVERAGE'
  setValueType: React.Dispatch<React.SetStateAction<'TOTAL' | 'AVERAGE'>>
  globalKpis: IKpiResponse[]
  setGlobalKpis: React.Dispatch<React.SetStateAction<IKpiResponse[]>>
}

export const KpiPromptContext = createContext<KpiPromptContextType>({
  activeMetric: kpiMetrics[0],
  setActiveMetric: () => {},
  period: PERIOD.HEBDO,
  setPeriod: () => {},
  currentDate: new Date(),
  setCurrentDate: () => {},
  start: new Date(),
  setStart: () => {},
  end: new Date(),
  setEnd: () => {},
  selectedPrompts: [],
  setSelectedPrompts: () => {},
  promptsList: [],
  setPromptsList: () => {},
  kpis: {},
  setKpis: () => {},
  oldGlobalKpis: [],
  setOldGlobalKpis: () => {},
  valueType: 'TOTAL',
  setValueType: () => {},
  globalKpis: [],
  setGlobalKpis: () => {},
})

export const KpiPromptContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [activeMetric, setActiveMetric] = useState(kpiMetrics[0])
  const [period, setPeriod] = useState<string>(PERIOD.HEBDO)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [startDate, endDate] = getPeriodDates(period, currentDate)
  const [start, setStart] = useState(startDate)
  const [end, setEnd] = useState(endDate)
  const [selectedPrompts, setSelectedPrompts] = useState<number[]>([])
  const [promptsList, setPromptsList] = useState<IPromptDetail[]>([])
  const [kpis, setKpis] = useState<Record<string, IKpiResponse[]>>({})
  const [oldGlobalKpis, setOldGlobalKpis] = useState<IKpiResponse[]>([])
  const [globalKpis, setGlobalKpis] = useState<IKpiResponse[]>([])
  const [valueType, setValueType] = useState<'TOTAL' | 'AVERAGE'>('TOTAL')
  useEffect(() => {
    const [newStartDate, newEndDate] = getPeriodDates(period, currentDate)
    setStart(newStartDate)
    setEnd(newEndDate)
  }, [period, currentDate])
  return (
    <KpiPromptContext.Provider
      value={{
        activeMetric,
        setActiveMetric,
        period,
        setPeriod,
        currentDate,
        setCurrentDate,
        start,
        setStart,
        end,
        setEnd,
        selectedPrompts,
        setSelectedPrompts,
        promptsList,
        setPromptsList,
        kpis,
        setKpis,
        oldGlobalKpis,
        setOldGlobalKpis,
        valueType,
        setValueType,
        globalKpis,
        setGlobalKpis,
      }}
    >
      {children}
    </KpiPromptContext.Provider>
  )
}
